import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Table,
    Card,
    Spin,
    Button,
    Input,
    Select,
    Row,
    Col,
    DatePicker,
    message,
    Image,
    Avatar,
    Checkbox,
    Popover,
    Divider,
    Badge,
    Modal,
    Form,
} from "antd";
import { AuthContext } from "../context/AuthContext";
import ViewEmpSalary from "../components/ViewEmpSalary";
import { useNavigate } from "react-router-dom";
import EditOneEmployee from "../components/EditOneEmployee";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FilePdfOutlined, FileExcelOutlined, SearchOutlined, ClearOutlined, SettingOutlined, ReloadOutlined, UserAddOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { UserOutlined } from "@ant-design/icons";


const { Option } = Select;

export default function ViewEmployees({ isResigned, isNoticePeriod }) {
    const { authData, backUrl } = useContext(AuthContext);
    const navigate = useNavigate();

    const [showSalary, setShowSalary] = useState(false);
    const [showEmp, setShowEmp] = useState(false);
    const [selectedId, setSelectedId] = useState(0);

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);

    const [excelLoading, setExcelLoading] = useState(false);

    const [selectedStatus, setSelectedStatus] = useState(
        isResigned ? "Resigned" : isNoticePeriod ? "Notice Period" : "All"
    );

    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [organizations, setOrganizations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [placesOfWork, setPlacesOfWork] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);
    const [selectedPlaceOfWork, setSelectedPlaceOfWork] = useState(null);
    const [selectedWorkLocation, setSelectedWorkLocation] = useState(null);

    const [searchCode, setSearchCode] = useState("");
    const [searchName, setSearchName] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const [showCalculator, setShowCalculator] = useState(false);
    const [showColumnPicker, setShowColumnPicker] = useState(false);

    // Add Employee Modal States
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [addForm] = Form.useForm();
    const [addLoading, setAddLoading] = useState(false);
    const [addFormDepartments, setAddFormDepartments] = useState([]);
    const [addFormDesignations, setAddFormDesignations] = useState([]);
    const [reportingEmployeesList, setReportingEmployeesList] = useState([]);

    const handleOpenAddModal = async () => {
        addForm.resetFields();
        setAddFormDepartments([]);
        setAddFormDesignations([]);
        setIsAddModalVisible(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/employees/mini_list/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setReportingEmployeesList(res.data.results || res.data || []);
        } catch (err) {
            console.error("Error fetching mini list:", err);
        }
    };

    const handleAddOrgChange = async (orgId) => {
        addForm.setFieldsValue({ department: undefined, designation: undefined });
        setAddFormDesignations([]);
        if (!orgId) {
            setAddFormDepartments([]);
            return;
        }
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setAddFormDepartments(res.data.results || res.data || []);
        } catch (err) {
            console.error("Error fetching departments for add form:", err);
            setAddFormDepartments([]);
        }
    };

    const handleAddDeptChange = async (deptId) => {
        addForm.setFieldsValue({ designation: undefined });
        if (!deptId) {
            setAddFormDesignations([]);
            return;
        }
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setAddFormDesignations(res.data.results || res.data || []);
        } catch (err) {
            console.error("Error fetching designations for add form:", err);
            setAddFormDesignations([]);
        }
    };

    const handleAddEmployeeSubmit = async () => {
        try {
            const values = await addForm.validateFields();
            setAddLoading(true);

            const payload = {
                salutation: values.salutation,
                first_name: values.first_name,
                last_name: values.last_name || "",
                email_id: values.official_email_id,
                official_email_id: values.official_email_id,
                mobile_number: values.mobile_number || "",
                gender: values.gender || null,
                date_of_birth: values.date_of_birth ? dayjs(values.date_of_birth).format("YYYY-MM-DD") : null,
                date_of_joining: values.date_of_joining ? dayjs(values.date_of_joining).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
                organisation: values.organisation || null,
                department: values.department || null,
                designation: values.designation || null,
                reporting_to: values.reporting_to || null,
                place_of_work: values.place_of_work ? (Array.isArray(values.place_of_work) ? values.place_of_work : [values.place_of_work]) : [],
                status: values.status || "Probation",
                is_active: true,
            };

            const res = await axios.post(`${backUrl}/api/emp/employees/`, payload, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            toast.success(`Employee ${res.data.emp_code || res.data.first_name || ''} created successfully!`);
            setIsAddModalVisible(false);
            addForm.resetFields();
            fetchEmployees();
        } catch (error) {
            console.error("Error creating employee:", error);
            const errMsg = error.response?.data?.message ||
                           error.response?.data?.detail ||
                           (typeof error.response?.data === 'object' ? JSON.stringify(error.response?.data) : "Failed to create employee");
            toast.error(errMsg);
        } finally {
            setAddLoading(false);
        }
    };

    // Column visibility — keys that are currently visible
    const ALL_COLUMN_KEYS = [
        // Basic Info
        "emp_code", "salutation", "initial", "first_name", "surname", "last_name",
        "organisation_name", "department_name", "designation_name", "location",
        "place_of_work", "date_of_joining", "is_hod", "reporting_to", "is_hr",
        "is_superuser", "role_id", "status", "probation_end_date", "is_active",
        "is_ot", "mobile_number",
        // Personal
        "email", "date_of_birth", "place_of_birth", "gender", "blood_group",
        "marital_status", "wedding_date", "mother_tongue", "caste", "religion",
        "height", "weight", "father_name", "father_profession",
        "mother_name", "mother_profession", "spouse_name", "spouse_dob",
        "child_details", "sibling_details",
        "emergency_contact_person", "emergency_contact_number",
        // Address
        "address_for_communication", "area", "city", "state", "country", "pincode",
        "present_address", "permanent_address_for_communication",
        "permanent_city", "permanent_state", "permanent_country", "permanent_pincode",
        // Financial
        "aadhaar_card_number", "pan_card_number", "bank_name",
        "bank_account_number", "branch_name", "ifsc_code",
        "cub_account_number", "uan_number", "esic_ip_number",
        // History
        "career_history", "reference_history", "education_history",
        // Documents
        "photo", "aadhar_card_copy", "appointment_letter", "bank_account_details",
        "covid_certificate", "degree_certificate", "last_three_month_payslip",
        "last_three_months_account_statement", "offer_letter", "pan_card_copy",
        "previous_experience_letter", "releiving_letter", "HSC_diploma_ITI", "PG_Phd", "SSLC",
        "others_1", "others2",
        // Contact/Other
        "landline_extension", "official_cug_number", "official_email_id",
        "is_management", "is_special", "is_shift",
        "date_of_leaving", "last_working_day",
    ];

    // Default visible columns (basic ones shown at start)
    const DEFAULT_VISIBLE_KEYS = [
        "emp_code", "salutation", "first_name", "surname", "last_name",
        "organisation_name", "department_name", "designation_name", "location",
        "place_of_work", "date_of_joining", "status", "mobile_number",
        "reporting_to", "is_hod", "is_hr", "is_active",
    ];

    const [visibleColumnKeys, setVisibleColumnKeys] = useState(
        () => new Set(ALL_COLUMN_KEYS) // All visible by default
    );

    const COLUMN_GROUPS = [
        {
            label: "Basic Info",
            keys: ["emp_code", "salutation", "initial", "first_name", "surname", "last_name",
                "organisation_name", "department_name", "designation_name", "location",
                "place_of_work", "date_of_joining", "is_hod", "reporting_to", "is_hr",
                "is_superuser", "role_id", "status", "probation_end_date", "is_active",
                "is_ot", "mobile_number"],
        },
        {
            label: "Personal",
            keys: ["email", "date_of_birth", "place_of_birth", "gender", "blood_group",
                "marital_status", "wedding_date", "mother_tongue", "caste", "religion",
                "height", "weight", "father_name", "father_profession",
                "mother_name", "mother_profession", "spouse_name", "spouse_dob",
                "child_details", "sibling_details",
                "emergency_contact_person", "emergency_contact_number"],
        },
        {
            label: "Address",
            keys: ["address_for_communication", "area", "city", "state", "country", "pincode",
                "present_address", "permanent_address_for_communication",
                "permanent_city", "permanent_state", "permanent_country", "permanent_pincode"],
        },
        {
            label: "Financial",
            keys: ["aadhaar_card_number", "pan_card_number", "bank_name",
                "bank_account_number", "branch_name", "ifsc_code",
                "cub_account_number", "uan_number", "esic_ip_number"],
        },
        {
            label: "History",
            keys: ["career_history", "reference_history", "education_history"],
        },
        {
            label: "Documents",
            keys: ["photo", "aadhar_card_copy", "appointment_letter", "bank_account_details",
                "covid_certificate", "degree_certificate", "last_three_month_payslip",
                "last_three_months_account_statement", "offer_letter", "pan_card_copy",
                "previous_experience_letter", "releiving_letter", "HSC_diploma_ITI", "PG_Phd", "SSLC",
                "others_1", "others2"],
        },
        {
            label: "Contact / Other",
            keys: ["landline_extension", "official_cug_number", "official_email_id",
                "is_management", "is_special", "is_shift",
                "date_of_leaving", "last_working_day"],
        },
    ];

    // Human-readable labels for column keys
    const COL_LABELS = {
        emp_code: "Employee Code", salutation: "Salutation", initial: "Initial",
        first_name: "First Name", surname: "Surname", last_name: "Last Name",
        organisation_name: "Organisation", department_name: "Department",
        designation_name: "Designation", location: "Location",
        place_of_work: "Place of Work", date_of_joining: "Date of Joining",
        is_hod: "Is HOD", reporting_to: "Reporting To", is_hr: "Is HR",
        is_superuser: "Is Superuser", role_id: "Role ID", status: "Status",
        probation_end_date: "Probation End Date", is_active: "Is Active",
        is_ot: "Is OT", mobile_number: "Mobile Number",
        email: "Email", date_of_birth: "Date of Birth", place_of_birth: "Place of Birth",
        gender: "Gender", blood_group: "Blood Group", marital_status: "Marital Status",
        wedding_date: "Wedding Date", mother_tongue: "Mother Tongue",
        caste: "Caste", religion: "Religion", height: "Height", weight: "Weight",
        father_name: "Father Name", father_profession: "Father Profession",
        mother_name: "Mother Name", mother_profession: "Mother Profession",
        spouse_name: "Spouse Name", spouse_dob: "Spouse DOB",
        child_details: "Child Details", sibling_details: "Siblings Details",
        emergency_contact_person: "Emergency Contact Person",
        emergency_contact_number: "Emergency Contact Number",
        address_for_communication: "Address", area: "Area", city: "City",
        state: "State", country: "Country", pincode: "Pincode",
        present_address: "Present Address",
        permanent_address_for_communication: "Permanent Address",
        permanent_city: "Permanent City", permanent_state: "Permanent State",
        permanent_country: "Permanent Country", permanent_pincode: "Permanent Pincode",
        aadhaar_card_number: "Aadhaar Card No.", pan_card_number: "PAN Card No.",
        bank_name: "Bank Name", bank_account_number: "Bank Account No.",
        branch_name: "Branch Name", ifsc_code: "IFSC Code",
        cub_account_number: "CUB Account No.", uan_number: "UAN Number",
        esic_ip_number: "ESIC IP No.",
        career_history: "Career History", reference_history: "Reference History",
        education_history: "Education History",
        photo: "Photo", aadhar_card_copy: "Aadhaar Copy",
        appointment_letter: "Appointment Letter", bank_account_details: "Bank Account Details",
        covid_certificate: "COVID Certificate", degree_certificate: "Degree Certificate",
        last_three_month_payslip: "Last 3M Payslip",
        last_three_months_account_statement: "Last 3M Statement",
        offer_letter: "Offer Letter", pan_card_copy: "PAN Copy",
        previous_experience_letter: "Exp. Letter", releiving_letter: "Relieving Letter",
        HSC_diploma_ITI: "HSC/Diploma/ITI", PG_Phd: "PG/PhD", SSLC: "SSLC",
        others_1: "Others 1", others2: "Others 2",
        landline_extension: "Landline Extension", official_cug_number: "Official CUG No.",
        official_email_id: "Official Email", is_management: "Is Management",
        is_special: "Is Special", is_shift: "Is Shift",
        date_of_leaving: "Date of Leaving", last_working_day: "Last Working Day",
    };
    const [particular, setParticular] = useState(null);

    // Helper function to parse JSON strings
    const parseJsonField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
            try {
                return JSON.parse(field);
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    // Build query parameters for API calls
    const buildQueryParams = (includePagination = true, statusOverride, pageOverride, clearAll = false) => {
        const params = new URLSearchParams();

        if (clearAll) {
            if (includePagination) {
                params.append("page", 1);
                params.append("page_size", pageSize);
            }
            const activeStatus = statusOverride !== undefined ? statusOverride : (isResigned ? "Resigned" : isNoticePeriod ? "Notice Period" : "All");
            if (activeStatus && activeStatus !== "All") {
                params.append("status", activeStatus);
            }
            return params.toString();
        }

        // Pagination parameters (only if requested)
        if (includePagination) {
            const activePage = pageOverride !== undefined ? pageOverride : currentPage;
            params.append("page", activePage);
            params.append("page_size", pageSize);
        }

        // Status filter - only add if it's not "All"
        const activeStatus = statusOverride !== undefined ? statusOverride : selectedStatus;
        if (activeStatus && activeStatus !== "All") {
            params.append("status", activeStatus);
        }

        // Search filters - trim whitespace
        if (searchCode && searchCode.trim()) {
            params.append("emp_code", searchCode.trim());
        }
        if (searchName && searchName.trim()) {
            params.append("employee_name", searchName.trim());
        }

        // Dropdown filters
        if (selectedOrg) {
            params.append("organisation", selectedOrg);
        }
        if (selectedDept) {
            params.append("department", selectedDept);
        }
        if (selectedDesig) {
            params.append("designation", selectedDesig);
        }
        if (selectedPlaceOfWork) {
            params.append("place_of_work", selectedPlaceOfWork);
        }
        if (selectedWorkLocation) {
            params.append("location", selectedWorkLocation);
        }

        // Date range filters
        if (fromDate) {
            params.append("from_date", dayjs(fromDate).format("YYYY-MM-DD"));
        }
        if (toDate) {
            params.append("to_date", dayjs(toDate).format("YYYY-MM-DD"));
        }

        return params.toString();
    };

    // Fetch employees with pagination
    const fetchEmployees = async (statusOverride, pageOverride, clearAll = false) => {
        try {
            setLoading(true);
            const queryString = buildQueryParams(true, statusOverride, pageOverride, clearAll);
            const url = `${backUrl}/api/emp/employees/${queryString ? `?${queryString}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            setEmployees(res.data.results || res.data);
            setTotalCount(res.data.count || res.data.length || 0);
        } catch (error) {
            console.error("Error fetching employees:", error);
            message.error("Failed to fetch employees");
        } finally {
            setLoading(false);
        }
    };

    // Fetch organizations
    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching organizations:", error);
        }
    };

    // Fetch departments based on organization
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setDepartments(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    // Fetch designations based on department
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    // Fetch places of work
    const fetchPlacesOfWork = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/worklocation/`);
            setPlacesOfWork(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching places of work:", error);
        }
    };

    // Initial load of config dropdowns
    useEffect(() => {
        fetchOrganizations();
        fetchPlacesOfWork();
    }, []);

    // Sync state and reload when props change or returning from edit view
    useEffect(() => {
        if (!showEmp) {
            const newStatus = isResigned ? "Resigned" : isNoticePeriod ? "Notice Period" : "All";
            setSelectedStatus(newStatus);
            setCurrentPage(1);
            fetchEmployees(newStatus, 1);
        }
    }, [isResigned, isNoticePeriod, showEmp]);

    // Fetch when page or page size changes
    useEffect(() => {
        if (currentPage > 1 || pageSize !== 50) {
            fetchEmployees();
        }
    }, [currentPage, pageSize]);

    // Helper to flatten array for Excel export
    const flattenArray = (arr, fields = []) => {
        if (!arr || !arr.length) return "-";
        return arr
            .map(item =>
                fields.map(f => `${f.label}: ${item[f.key] || "N/A"}`).join(", ")
            )
            .join(" | ");
    };

    // Helper to render values
    const renderValue = (value) => (value === null || value === undefined || value === "" ? "-" : value);

    // Handle Full & Final settlement
    const handleFullAndFinal = async (employeeId) => {
        try {
            setLoading(true);
            await axios.post(
                `${backUrl}/api/payroll/settlements/create/`,
                { employee_id: employeeId },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success("Full & Final settlement process triggered successfully.");
        } catch (error) {
            console.error("Error triggering settlement:", error);
            if (error.response?.data?.error) toast.error(` ${error.response.data.error}`);
            else toast.error("Failed to trigger Full & Final settlement. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Format row for export
    const formatRow = (row) => ({
        "Employee Code": row.user?.username || "-",
        "Salutation": row.salutation || "-",
        "Initial": row.initial || "-",
        "First Name": row.first_name || "-",
        "Surname": row.surname || "-",
        "Last Name": row.last_name || "-",
        "Organisation": row.organisation_name || "-",
        "Department": row.department_name || "-",
        "Designation": row.designation_name || "-",
        "Location": row.location || "-",
        "Place of Work": Array.isArray(row.place_of_work_names)
            ? row.place_of_work_names.join(", ")
            : row.place_of_work_names || "-",
        "Date of Joining": row.date_of_joining || "-",
        "Is HOD": row.is_hod ? "Yes" : "No",
        "Reporting To": row.reporting_to_name || "-",
        "Is HR": row.is_hr ? "Yes" : "No",
        "Is Superuser": row.is_superuser ? "Yes" : "No",
        "Role ID": row.role || "-",
        "Status": row.status || "-",
        "Probation End Date": row.probation_end_date || "-",
        "Is Active": row.is_active ? "Yes" : "No",
        "Is OT": row.is_ot ? "Yes" : "No",
        "Mobile Number": row.mobile_number || "-",
        "Email": row.user?.email || "-",
        "Date of Birth": row.date_of_birth || "-",
        "Place of Birth": row.place_of_birth || "-",
        "Mother Tongue": row.mother_tongue || "-",
        "Caste": row.caste || "-",
        "Gender": row.gender || "-",
        "Religion": row.religion || "-",
        "Blood Group": row.blood_group || "-",
        "Height": row.height || "-",
        "Weight": row.weight || "-",
        "Marital Status": row.marital_status || "-",
        "Wedding Date": row.wedding_date || "-",
        "Father Name": row.father_name || "-",
        "Father Profession": row.father_profession || "-",
        "Mother Name": row.mother_name || "-",
        "Mother Profession": row.mother_profession || "-",
        "Spouse Name": row.spouse_name || "-",
        "Spouse DOB": row.spouse_dob || "-",
        "Child Details": flattenArray(parseJsonField(row.child_details), [
            { key: "child_name", label: "Name" },
            { key: "child_dob", label: "DOB" },
            { key: "child_gender", label: "Gender" },
        ]),
        "Siblings Details": flattenArray(parseJsonField(row.sibling_details), [
            { key: "sibling_name", label: "Name" },
            { key: "sibling_dob", label: "DOB" },
            { key: "sibling_gender", label: "Gender" },
        ]),
        "Emergency Contact Person": row.emergency_contact_person || "-",
        "Emergency Contact Number": row.emergency_contact_number || "-",
        "Aadhaar Card Number": row.aadhaar_card_number || "-",
        "PAN Card Number": row.pan_card_number || "-",
        "Bank Name": row.bank_name || "-",
        "Bank Account Number": row.bank_account_number || "-",
        "Branch Name": row.branch_name || "-",
        "IFSC Code": row.ifsc_code || "-",
        "CUB Account Number": row.cub_account_number || "-",
        "UAN Number": row.uan_number || "-",
        "ESIC IP Number": row.esic_ip_number || "-",
        "Photo": row.photo || "-",
        "Address for Communication": row.address_for_communication || "-",
        "Area": row.area || "-",
        "City": row.city || "-",
        "State": row.state || "-",
        "Country": row.country || "-",
        "Pincode": row.pincode || "-",
        "Present Address": row.present_address ? "Yes" : "No",
        "Permanent Address": row.permanent_address_for_communication || "-",
        "Permanent City": row.permanent_city || "-",
        "Permanent State": row.permanent_state || "-",
        "Permanent Country": row.permanent_country || "-",
        "Permanent Pincode": row.permanent_pincode || "-",
        "Career History": Array.isArray(row.career_history) ? flattenArray(row.career_history, [
            { key: "organisation", label: "Organisation" },
            { key: "designation", label: "Designation" },
            { key: "work_duration_from", label: "From" },
            { key: "work_duration_to", label: "To" },
        ]) : (row.career_history || "-"),
        "Reference History": Array.isArray(row.reference_history) ? flattenArray(row.reference_history, [
            { key: "referal_name", label: "Name" },
            { key: "referal_desgination", label: "Designation" },
            { key: "referal_email_id", label: "Email" },
            { key: "referal_mobile_number", label: "Mobile" },
        ]) : (row.reference_history || "-"),
        "Education History": Array.isArray(row.education_history) ? flattenArray(row.education_history, [
            { key: "educational_qualification", label: "Qualification" },
            { key: "educational_institution_name", label: "Institution" },
            { key: "course_duration_from", label: "From" },
            { key: "course_duration_to", label: "To" },
        ]) : (row.education_history || "-"),
        "Date of Leaving": row.date_of_leaving || "-",
        "Last Working Day": row.last_working_day || "-",
        "Aadhaar Card Copy": row.aadhar_card_copy || "-",
        "Appointment Letter": row.appointment_letter || "-",
        "Bank Account Details": row.bank_account_details || "-",
        "COVID Certificate": row.covid_certificate || "-",
        "Degree Certificate": row.degree_certificate || "-",
        "Last 3 Month Payslip": row.last_three_month_payslip || "-",
        "Last 3 Months Account Statement": row.last_three_months_account_statement || "-",
        "Offer Letter": row.offer_letter || "-",
        "PAN Card Copy": row.pan_card_copy || "-",
        "Previous Experience Letter": row.previous_experience_letter || "-",
        "Relieving Letter": row.releiving_letter || "-",
        "Others 1": row.others_1 || "-",
        "Others 2": row.others2 || "-",
        "Landline Extension": row.landline_extension || "-",
        "Official CUG Number": row.official_cug_number || "-",
        "Official Email ID": row.official_email_id || "-",
        "Is Management": row.is_management || "-",
        "Is Special": row.is_special || "-",
        "Is Shift": row.is_shift || "-",
        "Is Active": row.is_active || "-",
    });

    // Export to Excel - fetches all data without pagination
    const handleExportExcel = async () => {
        try {
            setExcelLoading(true);
            message.loading({ content: 'Preparing Excel export...', key: 'excel' });

            // Build query without pagination
            const queryString = buildQueryParams(false);
            const url = `${backUrl}/api/emp/employees/${queryString ? `?${queryString}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            const allEmployees = res.data.results || res.data;

            if (!allEmployees || allEmployees.length === 0) {
                message.warning({ content: 'No employees to export', key: 'excel' });
                return;
            }

            const XLSX = require("xlsx");
            const formatted = allEmployees.map(formatRow);
            const ws = XLSX.utils.json_to_sheet(formatted);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Employees");
            XLSX.writeFile(wb, `Employees_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);

            message.success({ content: `Exported ${allEmployees.length} employees successfully!`, key: 'excel' });
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            message.error({ content: 'Failed to export Excel file', key: 'excel' });
        } finally {
            setExcelLoading(false);
        }
    };

    // Export to PDF
    const handleExportPDF = () => {
        if (!employees.length) {
            toast.warning("No employees to export");
            return;
        }

        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
        const formatted = employees.map(formatRow);
        const columns = Object.keys(formatRow(employees[0] || {}));

        const columnStyles = {};
        columns.forEach((_, index) => (columnStyles[index] = { cellWidth: 80 }));

        autoTable(doc, {
            head: [columns],
            body: formatted.map((row) => Object.values(row)),
            styles: {
                fontSize: 8,
                cellPadding: 6,
                overflow: "linebreak",
                valign: "middle"
            },
            headStyles: {
                fillColor: [14, 73, 123],
                textColor: "#FFFFFF",
                halign: "center",
                fontSize: 9
            },
            theme: "grid",
            startY: 30,
            columnStyles: columnStyles,
            pageBreak: "auto",
            horizontalPageBreak: true,
            horizontalPageBreakRepeat: true,
        });

        doc.save(`Employees_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
    };

    // Table columns definition
    const columns = [
        {
            title: "Employee Code",
            dataIndex: "emp_code",
            key: "emp_code",
            fixed: 'left',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                    <Avatar
                        size={50}
                        src={record.photo}
                        icon={!record.photo && <UserOutlined />}
                        style={{
                            backgroundColor: !record.photo ? '#1890ff' : undefined,
                            flexShrink: 0
                        }}
                    />
                    <a
                        onClick={() => {
                            setSelectedId(record.id);
                            setShowEmp(true);
                        }}
                        style={{ cursor: "pointer", color: '#1890ff' }}
                    >
                        {/* Prefer emp_code from Employee row; fallback to user.username */}
                        {record.emp_code || record.user?.username || text || "-"}
                    </a>
                </div>
            ),
            height: 30,
            width: 180,
        },
        { title: "Salutation", dataIndex: "salutation", key: "salutation", render: renderValue, width: 100 },
        { title: "Initial", dataIndex: "initial", key: "initial", render: renderValue, width: 100 },
        { title: "First Name", dataIndex: "first_name", key: "first_name", render: renderValue },
        { title: "Surname", dataIndex: "surname", key: "surname", render: renderValue },
        { title: "Last Name", dataIndex: "last_name", key: "last_name", render: renderValue },
        { title: "Organisation", dataIndex: "organisation_name", key: "organisation_name", render: renderValue },
        { title: "Department", dataIndex: "department_name", key: "department_name", render: renderValue },
        { title: "Designation", dataIndex: "designation_name", key: "designation_name", render: renderValue },
        { title: "Location", dataIndex: "location", key: "location", render: renderValue },
        { title: "Place of Work", dataIndex: "place_of_work_names", key: "place_of_work", render: renderValue },
        { title: "Date of Joining", dataIndex: "date_of_joining", key: "date_of_joining", render: renderValue },
        { title: "Is HOD", dataIndex: "is_hod", key: "is_hod", render: (v) => v ? "Yes" : "No" },
        { title: "Reporting To", dataIndex: "reporting_to_name", key: "reporting_to", render: renderValue },
        { title: "Is HR", dataIndex: "is_hr", key: "is_hr", render: (v) => v ? "Yes" : "No" },
        { title: "Is Superuser", dataIndex: "is_superuser", key: "is_superuser", render: (v) => v ? "Yes" : "No" },
        { title: "Role ID", dataIndex: "role", key: "role_id", render: renderValue },
        { title: "Status", dataIndex: "status", key: "status", render: renderValue },
        { title: "Probation End Date", dataIndex: "probation_end_date", key: "probation_end_date", render: renderValue },
        { title: "Is Active", dataIndex: "is_active", key: "is_active", render: (v) => v ? "Yes" : "No" },
        { title: "Is OT", dataIndex: "is_ot", key: "is_ot", render: (v) => v ? "Yes" : "No" },
        { title: "Mobile Number", dataIndex: "mobile_number", key: "mobile_number", render: renderValue },
        { title: "Email", dataIndex: "email_id", key: "email_id", render: renderValue },
        { title: "Date of Birth", dataIndex: "date_of_birth", key: "date_of_birth", render: renderValue },
        { title: "Place of Birth", dataIndex: "place_of_birth", key: "place_of_birth", render: renderValue },
        { title: "Mother Tongue", dataIndex: "mother_tongue", key: "mother_tongue", render: renderValue },
        { title: "Caste", dataIndex: "caste", key: "caste", render: renderValue },
        { title: "Gender", dataIndex: "gender", key: "gender", render: renderValue },
        { title: "Religion", dataIndex: "religion", key: "religion", render: renderValue },
        { title: "Blood Group", dataIndex: "blood_group", key: "blood_group", render: renderValue },
        { title: "Height", dataIndex: "height", key: "height", render: renderValue },
        { title: "Weight", dataIndex: "weight", key: "weight", render: renderValue },
        { title: "Marital Status", dataIndex: "marital_status", key: "marital_status", render: renderValue },
        { title: "Wedding Date", dataIndex: "wedding_date", key: "wedding_date", render: renderValue },
        { title: "Father Name", dataIndex: "father_name", key: "father_name", render: renderValue },
        { title: "Father Profession", dataIndex: "father_profession", key: "father_profession", render: renderValue },
        { title: "Mother Name", dataIndex: "mother_name", key: "mother_name", render: renderValue },
        { title: "Mother Profession", dataIndex: "mother_profession", key: "mother_profession", render: renderValue },
        { title: "Spouse Name", dataIndex: "spouse_name", key: "spouse_name", render: renderValue },
        { title: "Spouse DOB", dataIndex: "spouse_dob", key: "spouse_dob", render: renderValue },
        {
            title: "Child Details",
            dataIndex: "child_details",
            key: "child_details",
            render: (data) => {
                const list = parseJsonField(data);
                if (!list.length) return "-";
                return list.map((item, idx) => {
                    const parts = [];
                    if (item.child_name) parts.push(item.child_name);
                    if (item.child_dob) parts.push(item.child_dob);
                    if (item.child_gender) parts.push(item.child_gender);
                    return parts.length ? <div key={idx}>{parts.join(" | ")}</div> : <div key={idx}>-</div>;
                });
            }
        },
        {
            title: "Siblings Details",
            dataIndex: "sibling_details",
            key: "sibling_details",
            render: (data) => {
                const list = parseJsonField(data);
                if (!list.length) return "-";
                return list.map((item, idx) => {
                    const parts = [];
                    if (item.sibling_name) parts.push(item.sibling_name);
                    if (item.sibling_dob) parts.push(item.sibling_dob);
                    if (item.sibling_gender) parts.push(item.sibling_gender);
                    if (item.sibling_profession) parts.push(item.sibling_profession);
                    return parts.length ? <div key={idx}>{parts.join(" | ")}</div> : <div key={idx}>-</div>;
                });
            }
        },
        { title: "Emergency Contact Person", dataIndex: "emergency_contact_person", key: "emergency_contact_person", render: renderValue },
        { title: "Emergency Contact Number", dataIndex: "emergency_contact_number", key: "emergency_contact_number", render: renderValue },
        { title: "Aadhaar Card Number", dataIndex: "aadhaar_card_number", key: "aadhaar_card_number", render: renderValue },
        { title: "PAN Card Number", dataIndex: "pan_card_number", key: "pan_card_number", render: renderValue },
        { title: "Bank Name", dataIndex: "bank_name", key: "bank_name", render: renderValue },
        { title: "Bank Account Number", dataIndex: "bank_account_number", key: "bank_account_number", render: renderValue },
        { title: "Branch Name", dataIndex: "branch_name", key: "branch_name", render: renderValue },
        { title: "IFSC Code", dataIndex: "ifsc_code", key: "ifsc_code", render: renderValue },
        { title: "CUB Account Number", dataIndex: "cub_account_number", key: "cub_account_number", render: renderValue },
        { title: "UAN Number", dataIndex: "uan_number", key: "uan_number", render: renderValue },
        { title: "ESIC IP Number", dataIndex: "esic_ip_number", key: "esic_ip_number", render: renderValue },
        { title: "Photo", dataIndex: "photo", key: "photo", render: renderValue },
        { title: "Address", dataIndex: "address_for_communication", key: "address_for_communication", render: renderValue },
        { title: "Area", dataIndex: "area", key: "area", render: renderValue },
        { title: "City", dataIndex: "city", key: "city", render: renderValue },
        { title: "State", dataIndex: "state", key: "state", render: renderValue },
        { title: "Country", dataIndex: "country", key: "country", render: renderValue },
        { title: "Pincode", dataIndex: "pincode", key: "pincode", render: renderValue },
        { title: "Present Address", dataIndex: "present_address", key: "present_address", render: (v) => v ? "Yes" : "No" },
        { title: "Permanent Address", dataIndex: "permanent_address_for_communication", key: "permanent_address_for_communication", render: renderValue },
        { title: "Permanent City", dataIndex: "permanent_city", key: "permanent_city", render: renderValue },
        { title: "Permanent State", dataIndex: "permanent_state", key: "permanent_state", render: renderValue },
        { title: "Permanent Country", dataIndex: "permanent_country", key: "permanent_country", render: renderValue },
        { title: "Permanent Pincode", dataIndex: "permanent_pincode", key: "permanent_pincode", render: renderValue },
        {
            title: "Career History",
            dataIndex: "career_history",
            key: "career_history",
            render: (arr) => {
                const list = Array.isArray(arr) ? arr : [];
                if (!list.length) return "-";
                return list.map((item, idx) => (
                    <div key={idx}>
                        {item.organisation || "N/A"} | {item.designation || "N/A"} | {item.work_duration_from || "N/A"} to {item.work_duration_to || "N/A"}
                    </div>
                ));
            }
        },
        {
            title: "Reference History",
            dataIndex: "reference_history",
            key: "reference_history",
            render: (arr) => {
                const list = Array.isArray(arr) ? arr : [];
                if (!list.length) return "-";
                return list.map((item, idx) => (
                    <div key={idx}>
                        {item.referal_name || "N/A"} | {item.referal_desgination || "N/A"} | {item.referal_mobile_number || "N/A"} | {item.referal_email_id || "N/A"}
                    </div>
                ));
            }
        },
        {
            title: "Education History",
            dataIndex: "education_history",
            key: "education_history",
            render: (arr) => {
                const list = Array.isArray(arr) ? arr : [];
                if (!list.length) return "-";
                return list.map((item, idx) => (
                    <div key={idx}>
                        {item.educational_qualification || "N/A"} - {item.educational_institution_name || "N/A"} ({item.course_duration_from || "N/A"} to {item.course_duration_to || "N/A"})
                    </div>
                ));
            }
        },
        { title: "Date of Leaving", dataIndex: "date_of_leaving", key: "date_of_leaving", render: renderValue },
        { title: "Last Working Day", dataIndex: "last_working_day", key: "last_working_day", render: renderValue },
        { title: "HSC/Diploma/ITI", dataIndex: "HSC_diploma_ITI", key: "HSC_diploma_ITI", render: renderValue },
        { title: "PG/PhD", dataIndex: "PG_Phd", key: "PG_Phd", render: renderValue },
        { title: "SSLC", dataIndex: "SSLC", key: "SSLC", render: renderValue },
        {
            title: "Aadhaar Card Copy",
            dataIndex: "aadhar_card_copy",
            key: "aadhar_card_copy",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Appointment Letter",
            dataIndex: "appointment_letter",
            key: "appointment_letter",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Bank Account Details",
            dataIndex: "bank_account_details",
            key: "bank_account_details",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "COVID Certificate",
            dataIndex: "covid_certificate",
            key: "covid_certificate",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Degree Certificate",
            dataIndex: "degree_certificate",
            key: "degree_certificate",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Last 3 Month Payslip",
            dataIndex: "last_three_month_payslip",
            key: "last_three_month_payslip",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Last 3 Months Account Statement",
            dataIndex: "last_three_months_account_statement",
            key: "last_three_months_account_statement",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Offer Letter",
            dataIndex: "offer_letter",
            key: "offer_letter",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "PAN Card Copy",
            dataIndex: "pan_card_copy",
            key: "pan_card_copy",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Previous Experience Letter",
            dataIndex: "previous_experience_letter",
            key: "previous_experience_letter",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        {
            title: "Relieving Letter",
            dataIndex: "releiving_letter",
            key: "releiving_letter",
            render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">Download</a> : "-"
        },
        { title: "Others 1", dataIndex: "others_1", key: "others_1", render: renderValue },
        { title: "Others 2", dataIndex: "others2", key: "others2", render: renderValue },
        { title: "Landline Extension", dataIndex: "landline_extension", key: "landline_extension", render: renderValue },
        { title: "Official CUG Number", dataIndex: "official_cug_number", key: "official_cug_number", render: renderValue },
        { title: "Official Email ID", dataIndex: "official_email_id", key: "official_email_id", render: renderValue },
        { title: "Is Management", dataIndex: "is_management", key: "is_management", render: (v) => v ? "Yes" : "No" },
        { title: "Is Special", dataIndex: "is_special", key: "is_special", render: (v) => v ? "Yes" : "No" },
        { title: "Is Shift", dataIndex: "is_shift", key: "is_shift", render: (v) => v ? "Yes" : "No" },
        { title: "Is Active", dataIndex: "is_active", key: "is_active", render: (v) => v ? "Yes" : "No" },
    ];

    // Filter columns by visibility and ensure clean dynamic column widths
    const visibleColumns = columns
        .filter(col => visibleColumnKeys.has(col.key))
        .map(col => ({
            ...col,
            width: col.width || 160,
        }));

    const onBack = () => {
        setShowEmp(false);
    };

    // Clear all filters and reset to initial state
    const handleClearFilters = () => {
        setSearchCode("");
        setSearchName("");
        setSelectedOrg(null);
        setSelectedDept(null);
        setSelectedDesig(null);
        setSelectedPlaceOfWork(null);
        setSelectedWorkLocation(null);
        setFromDate(null);
        setToDate(null);
        const defaultStatus = isResigned ? "Resigned" : isNoticePeriod ? "Notice Period" : "All";
        setSelectedStatus(defaultStatus);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        setPageSize(50);

        // Instantly fetch employees with cleared parameters
        fetchEmployees(defaultStatus, 1, true);
    };

    // Apply filters
    const handleFilter = () => {
        setCurrentPage(1); // Reset to first page when filtering
        fetchEmployees();
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            {showEmp ? (
                selectedId !== 0 && <EditOneEmployee isResigned={isResigned} isNoticePeriod={isNoticePeriod} employeeId={selectedId} onBack={() => { setShowEmp(false); setSelectedId(0); fetchEmployees(); }} />
            ) : showCalculator ? (
                <>
                    <Button onClick={() => { setShowCalculator(false); setParticular(null); }} style={{ marginBottom: "10px" }}>
                        ← Back
                    </Button>
                    <ViewEmpSalary amount={particular.offered_salary} cid={particular.id} />
                </>
            ) : (
                <>
                    <Card bordered style={{ marginBottom: 20, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                        <Row style={{ marginBottom: 10 }} justify="space-between" align="middle">
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<FileExcelOutlined />}
                                    onClick={handleExportExcel}
                                    loading={excelLoading}
                                    style={{ marginRight: 8, background: 'green', borderColor: 'green' }}
                                />
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleFilter}
                                    style={{ marginRight: 8, backgroundColor: '#0e497b', borderColor: '#0e497b' }}
                                >
                                    Filter
                                </Button>
                                <Button
                                    onClick={handleClearFilters}
                                    style={{ backgroundColor: '#f5f5f5', color: '#000', marginRight: 8 }}
                                >
                                    Clear Filters
                                </Button>
                                <Popover
                                    open={showColumnPicker}
                                    onOpenChange={setShowColumnPicker}
                                    trigger="click"
                                    placement="bottomLeft"
                                    title={
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 340 }}>
                                            <span style={{ fontWeight: 600 }}>Show / Hide Columns</span>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <Button size="small" onClick={() => setVisibleColumnKeys(new Set(ALL_COLUMN_KEYS))}>Show All</Button>
                                                <Button size="small" onClick={() => setVisibleColumnKeys(new Set(DEFAULT_VISIBLE_KEYS))}>Reset Default</Button>
                                            </div>
                                        </div>
                                    }
                                    content={
                                        <div style={{ maxHeight: 420, overflowY: 'auto', width: 380 }}>
                                            {COLUMN_GROUPS.map((group, gi) => (
                                                <div key={gi} style={{ marginBottom: 12 }}>
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center',
                                                        gap: 6, marginBottom: 6,
                                                    }}>
                                                        <Checkbox
                                                            indeterminate={
                                                                group.keys.some(k => visibleColumnKeys.has(k)) &&
                                                                !group.keys.every(k => visibleColumnKeys.has(k))
                                                            }
                                                            checked={group.keys.every(k => visibleColumnKeys.has(k))}
                                                            onChange={e => {
                                                                const next = new Set(visibleColumnKeys);
                                                                if (e.target.checked) {
                                                                    group.keys.forEach(k => next.add(k));
                                                                } else {
                                                                    group.keys.forEach(k => {
                                                                        if (k !== 'emp_code') next.delete(k);
                                                                    });
                                                                }
                                                                setVisibleColumnKeys(next);
                                                            }}
                                                        />
                                                        <span style={{ fontWeight: 600, color: '#0e497b', fontSize: 13 }}>{group.label}</span>
                                                    </div>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 1fr',
                                                        gap: '4px 12px',
                                                        paddingLeft: 22
                                                    }}>
                                                        {group.keys.map(k => (
                                                            <Checkbox
                                                                key={k}
                                                                checked={visibleColumnKeys.has(k)}
                                                                disabled={k === 'emp_code'}
                                                                onChange={e => {
                                                                    const next = new Set(visibleColumnKeys);
                                                                    if (e.target.checked) next.add(k);
                                                                    else next.delete(k);
                                                                    setVisibleColumnKeys(next);
                                                                }}
                                                                style={{ fontSize: 12 }}
                                                            >
                                                                {COL_LABELS[k] || k}
                                                            </Checkbox>
                                                        ))}
                                                    </div>
                                                    {gi < COLUMN_GROUPS.length - 1 && <Divider style={{ margin: '8px 0' }} />}
                                                </div>
                                            ))}
                                        </div>
                                    }
                                >
                                    <Button
                                        icon={<SettingOutlined />}
                                        style={{ backgroundColor: '#0e497b', color: 'white', borderColor: '#0e497b' }}
                                    >
                                        Columns
                                        {visibleColumnKeys.size < ALL_COLUMN_KEYS.length && (
                                            <Badge
                                                count={ALL_COLUMN_KEYS.length - visibleColumnKeys.size}
                                                size="small"
                                                style={{ marginLeft: 4, backgroundColor: '#ff6b6b' }}
                                            />
                                        )}
                                    </Button>
                                </Popover>
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<UserAddOutlined />}
                                    onClick={handleOpenAddModal}
                                    style={{ backgroundColor: '#0e497b', borderColor: '#0e497b', fontWeight: '600' }}
                                >
                                    Add Employee
                                </Button>
                            </Col>
                        </Row>

                        <Row gutter={16} style={{ marginBottom: 10 }}>
                            <Col span={6}>
                                <Input
                                    allowClear
                                    placeholder="Search Employee Code"
                                    value={searchCode}
                                    onChange={(e) => setSearchCode(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>
                            <Col span={6}>
                                <Input
                                    allowClear
                                    placeholder="Search Employee Name"
                                    value={searchName}
                                    onChange={(e) => setSearchName(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>
                            <Col span={6}>
                                <Select
                                    allowClear
                                    showSearch
                                    placeholder="Select Organization"
                                    style={{ width: "100%" }}
                                    value={selectedOrg}
                                    onChange={(val) => {
                                        setSelectedOrg(val);
                                        setSelectedDept(null);
                                        setSelectedDesig(null);
                                        setDepartments([]);
                                        setDesignations([]);
                                        if (val) fetchDepartments(val);
                                    }}
                                    filterOption={(input, option) => {
                                        const text = option?.children ?? option?.label ?? "";
                                        return String(text).toLowerCase().includes((input || "").toLowerCase());
                                    }}
                                >
                                    {organizations.map((o) => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select Department"
                                    style={{ width: "100%" }}
                                    value={selectedDept}
                                    onChange={(val) => {
                                        setSelectedDept(val);
                                        setSelectedDesig(null);
                                        setDesignations([]);
                                        if (val) fetchDesignations(val);
                                    }}
                                    disabled={!selectedOrg}
                                    filterOption={(input, option) => {
                                        const text = option?.children ?? option?.label ?? "";
                                        return String(text).toLowerCase().includes((input || "").toLowerCase());
                                    }}
                                >
                                    {departments.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                                </Select>
                            </Col>
                        </Row>

                        <Row gutter={16} style={{ marginBottom: 10 }}>
                            <Col span={6}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select Designation"
                                    style={{ width: "100%" }}
                                    value={selectedDesig}
                                    onChange={(val) => setSelectedDesig(val)}
                                    disabled={!selectedDept}
                                    filterOption={(input, option) => {
                                        const text = option?.children ?? option?.label ?? "";
                                        return String(text).toLowerCase().includes((input || "").toLowerCase());
                                    }}
                                >
                                    {designations.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    allowClear
                                    placeholder="Select Work Location"
                                    style={{ width: "100%" }}
                                    value={selectedWorkLocation}
                                    onChange={(val) => setSelectedWorkLocation(val)}
                                >
                                    <Option value="Head Office">Head Office</Option>
                                    <Option value="Site Office">Site Office</Option>
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select Place of Work"
                                    style={{ width: "100%" }}
                                    value={selectedPlaceOfWork}
                                    onChange={(val) => setSelectedPlaceOfWork(val)}
                                    filterOption={(input, option) => {
                                        const text = option?.children ?? option?.label ?? "";
                                        return String(text).toLowerCase().includes((input || "").toLowerCase());
                                    }}
                                >
                                    {placesOfWork.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <Select
                                    allowClear
                                    placeholder="Select Employment Status"
                                    style={{ width: "100%" }}
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                >
                                    <Option value="All">All</Option>
                                    <Option value="Confirmed">Confirmed</Option>
                                    <Option value="Probation">Probation</Option>
                                    <Option value="Resigned">Resigned</Option>
                                    <Option value="Notice Period">Notice Period</Option>
                                    <Option value="Separated">Separated</Option>
                                </Select>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={6}>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    placeholder="From Date (Joining)"
                                    value={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    allowClear
                                    format="YYYY-MM-DD"
                                />
                            </Col>
                            <Col span={6}>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    placeholder="To Date (Joining)"
                                    value={toDate}
                                    onChange={(date) => setToDate(date)}
                                    allowClear
                                    format="YYYY-MM-DD"
                                />
                            </Col>
                        </Row>
                    </Card>

                    {loading ? (
                        <div style={{ textAlign: "center", padding: "40px" }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            rowKey="id"
                            dataSource={employees}
                            columns={visibleColumns}
                            bordered
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: totalCount,
                                showSizeChanger: true,
                                pageSizeOptions: ["8", "10", "20", "50", "100"],
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                },
                                onShowSizeChange: (_, size) => {
                                    setPageSize(size);
                                    setCurrentPage(1);
                                },
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            scroll={{ x: visibleColumns.reduce((acc, col) => acc + (col.width || 160), 0) }}
                            components={{
                                header: {
                                    cell: (props) => (
                                        <th {...props} style={{ backgroundColor: "#0e497b", color: 'white' }}>
                                            {props.children}
                                        </th>
                                    ),
                                },
                            }}
                        />
                    )}
                </>
            )}

            {/* ── Add Employee Modal ── */}
            <Modal
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0e497b', fontWeight: 700, fontSize: 18 }}>
                        <UserAddOutlined style={{ fontSize: 20 }} />
                        <span>Add New Employee</span>
                    </div>
                }
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form
                    layout="vertical"
                    form={addForm}
                    onFinish={handleAddEmployeeSubmit}
                    initialValues={{ status: "Probation", date_of_joining: dayjs() }}
                    style={{ marginTop: 12 }}
                >
                    <Card size="small" title="1. Personal & Contact Information" style={{ marginBottom: 16, backgroundColor: '#f8fafc', borderRadius: 8 }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="Salutation" name="salutation">
                                    <Select placeholder="Select">
                                        <Option value="Mr.">Mr.</Option>
                                        <Option value="Ms.">Ms.</Option>
                                        <Option value="Mrs.">Mrs.</Option>
                                        <Option value="Dr.">Dr.</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={9}>
                                <Form.Item
                                    label="First Name"
                                    name="first_name"
                                    rules={[{ required: true, message: "First name is required!" }]}
                                >
                                    <Input placeholder="First Name" />
                                </Form.Item>
                            </Col>
                            <Col span={9}>
                                <Form.Item label="Last Name" name="last_name">
                                    <Input placeholder="Last Name" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Official Email"
                                    name="official_email_id"
                                    rules={[
                                        { required: true, message: "Official email is required!" },
                                        { type: "email", message: "Enter a valid email!" }
                                    ]}
                                >
                                    <Input placeholder="official@company.com" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Mobile Number" name="mobile_number">
                                    <Input placeholder="Mobile Number" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Gender" name="gender">
                                    <Select placeholder="Select Gender" allowClear>
                                        <Option value="MALE">Male</Option>
                                        <Option value="FEMALE">Female</Option>
                                        <Option value="OTHER">Other</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Date of Birth" name="date_of_birth">
                                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder="Date of Birth" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Date of Joining"
                                    name="date_of_joining"
                                    rules={[{ required: true, message: "Date of joining is required!" }]}
                                >
                                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder="Date of Joining" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card size="small" title="2. Organization & Work Setup" style={{ marginBottom: 16, backgroundColor: '#f8fafc', borderRadius: 8 }}>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Organisation"
                                    name="organisation"
                                    rules={[{ required: true, message: "Organisation is required!" }]}
                                >
                                    <Select
                                        placeholder="Select Organisation"
                                        onChange={handleAddOrgChange}
                                        allowClear
                                    >
                                        {organizations.map(org => (
                                            <Option key={org.id} value={org.id}>{org.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Department"
                                    name="department"
                                    rules={[{ required: true, message: "Department is required!" }]}
                                >
                                    <Select
                                        placeholder="Select Department"
                                        onChange={handleAddDeptChange}
                                        disabled={!addFormDepartments.length}
                                        allowClear
                                    >
                                        {addFormDepartments.map(dept => (
                                            <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Designation"
                                    name="designation"
                                    rules={[{ required: true, message: "Designation is required!" }]}
                                >
                                    <Select
                                        placeholder="Select Designation"
                                        disabled={!addFormDesignations.length}
                                        allowClear
                                    >
                                        {addFormDesignations.map(desig => (
                                            <Option key={desig.id} value={desig.id}>{desig.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Place of Work" name="place_of_work">
                                    <Select
                                        placeholder="Select Place of Work"
                                        allowClear
                                    >
                                        {placesOfWork.map(pow => (
                                            <Option key={pow.id} value={pow.id}>{pow.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Reporting To" name="reporting_to">
                                    <Select
                                        showSearch
                                        placeholder="Select Manager / Reporting Lead"
                                        allowClear
                                        filterOption={(input, option) => {
                                            if (!input) return true;
                                            const searchStr = input.toLowerCase();
                                            const text = option?.label || option?.children || "";
                                            if (Array.isArray(text)) {
                                                return text.join(" ").toLowerCase().includes(searchStr);
                                            }
                                            return String(text).toLowerCase().includes(searchStr);
                                        }}
                                    >
                                        {reportingEmployeesList.map(emp => {
                                            const empCodeStr = emp.emp_code ? `[${emp.emp_code}] ` : '';
                                            const fullNameStr = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                                            const labelText = `${empCodeStr}${fullNameStr}`;
                                            return (
                                                <Option key={emp.id} value={emp.id} label={labelText}>
                                                    {labelText}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Employment Status" name="status">
                                    <Select placeholder="Select Status">
                                        <Option value="Probation">Probation</Option>
                                        <Option value="Confirmed">Confirmed</Option>
                                        <Option value="Trainee">Trainee</Option>
                                        <Option value="Intern">Intern</Option>
                                        <Option value="Contract">Contract</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <div style={{ textAlign: "right", marginTop: 16 }}>
                        <Button onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }} style={{ marginRight: 10 }}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={addLoading}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", fontWeight: "600" }}
                        >
                            Save Employee
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}